import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useLocation } from "react-router";

import type {
  AppBrandingDetailsDto,
  AppBrandingDto,
  ConnectedProjectDto,
  LanguageDto,
  ProjectDetailedDto,
} from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { IconButton } from "@/components/Button/IconButton";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { Form } from "@/components/Form/Form";
import { FormInput } from "@/components/Form/FormInput";
import { FormInputAutocomplete } from "@/components/Form/FormInputAutocomplete";
import { FormRadioCard, FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import { FormSearchableSelect } from "@/components/Form/FormSearchableSelect";
import { FormSelect } from "@/components/Form/FormSelect";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Inline } from "@/design-system/Inline";
import { prepareSuggestions } from "@/design-system/InputAutocomplete";
import { Spread } from "@/design-system/Spread";
import { canManageProjectManagement } from "@/helpers/permissions";
import { createRequiredStringRule } from "@/helpers/rules";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { useProjectQueries } from "@/queries/project/queryOptions";
import type { TranslationFunction } from "@/translations";
import { Trans, useTranslation } from "@/translations";

import { CloseProjectModal } from "./components/CloseProjectModal";
import { SlugModal } from "./SlugModal";

export interface CreateOrEditProjectFormValues {
  name: string;
  slug?: string;
  city: string;
  owner: string;
  maintenance: string;
  language: LanguageDto;
  type: ProjectDetailedDto["type"];
  cloneProject?: ConnectedProjectDto;
  activeState: ProjectDetailedDto["activeState"];
  appBranding: AppBrandingDto;
  developmentType: ProjectDetailedDto["developmentType"];
  allowHidePostForAdmin: "true" | "false";
  housemateInvitesEnabled: "true" | "false";
}

export interface ProjectFormProps {
  baseLanguages: LanguageDto[];
  projects: ConnectedProjectDto[];
  isEditMode: boolean;
  defaultValues: Partial<CreateOrEditProjectFormValues>;
  projectState: ProjectDetailedDto["activeState"];
  isSubmitting: boolean;
  onSubmit: (data: CreateOrEditProjectFormValues) => Promise<void>;
  closeProject: (deleteUsers: boolean) => Promise<unknown>;
  isClosingProject: boolean;
  appBrands: AppBrandingDetailsDto[];
}

export function ProjectForm({
  isEditMode,
  defaultValues,
  baseLanguages: languages,
  projects,
  projectState,
  isSubmitting,
  onSubmit,
  closeProject,
  isClosingProject,
  appBrands,
}: ProjectFormProps): React.ReactNode {
  const [isSlugModalOpen, slugModalHandlers] = useBool();
  const [isActivateProjectModalOpen, activateProjectModalHandlers] = useBool();
  const [isSetProjectToTestModalOpen, setProjectToTestModalHandlers] = useBool();
  const [isCloseProjectModalOpened, closeProjectModalHandlers] = useBool();

  const { pathname } = useLocation();
  const sessionUser = useSessionUser();
  const { t } = useTranslation();
  const formMethods = useForm<CreateOrEditProjectFormValues>({ defaultValues });
  const language = useWatch<CreateOrEditProjectFormValues, "language">({
    control: formMethods.control,
    name: "language",
  });
  const cloneProject = useWatch<CreateOrEditProjectFormValues, "cloneProject">({
    control: formMethods.control,
    name: "cloneProject",
  });
  const owner = useWatch<CreateOrEditProjectFormValues, "owner">({ control: formMethods.control, name: "owner" });
  const hasPermission = usePermission();
  const projectQueries = useProjectQueries();

  const allowedCloneProjects = useMemo(
    () => projects.filter((x) => x.projectBaseLanguageId === language.id),
    [projects, language.id],
  );

  const { setValue } = formMethods;
  useEffect(() => {
    if (cloneProject?.id && !allowedCloneProjects.some((x) => x.id === cloneProject.id)) {
      setValue("cloneProject", undefined);
    }
  }, [cloneProject?.id, allowedCloneProjects, setValue]);

  const { data: cloneProjectDetails } = useQuery({
    ...projectQueries.details(cloneProject?.id ?? "-"),
    enabled: !!cloneProject?.id && !isEditMode,
  });

  const { dirtyFields } = formMethods.formState;
  useEffect(() => {
    if (cloneProjectDetails && !isEditMode) {
      if (!dirtyFields.owner) {
        setValue("owner", cloneProjectDetails.owner);
      }
      if (!dirtyFields.maintenance) {
        setValue("maintenance", cloneProjectDetails.maintenance);
      }

      if (!dirtyFields.appBranding) {
        setValue("appBranding", cloneProjectDetails.appBranding);
      }
    }
  }, [cloneProjectDetails, isEditMode, setValue, dirtyFields]);

  const citySuggestions = useMemo(() => prepareSuggestions(projects.map((x) => x.city)), [projects]);
  const ownerSuggestions = useMemo(() => prepareSuggestions(projects.map((x) => x.owner)), [projects]);
  const maintenanceSuggestions = useMemo(() => {
    const sameOwnerProjects = projects.filter((x) => x.owner === owner);

    return prepareSuggestions(sameOwnerProjects.map((x) => x.maintenance));
  }, [projects, owner]);

  const takenSlugs = useMemo(() => projects.map((x) => x.slug), [projects]);
  const projectStateOptions: CreateOrEditProjectFormValues["activeState"][] =
    projectState === "unknown" ? ["unknown", "active", "test", "demo"] : ["active", "test", "demo"];

  const title = isEditMode ? t("page.projects.edit.title") : t("page.projects.new.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            ...(pathname.includes("portfolio")
              ? [
                  {
                    name: t("page.portfolio.title"),
                  },
                ]
              : [
                  {
                    name: sessionUser.project.name,
                  },
                ]),
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={formMethods} onSubmit={onSubmit}>
        <Form.Container>
          <Form.Body>
            <FormField label={t("page.projects.form.name")} required>
              <FormInput<CreateOrEditProjectFormValues, "name">
                data-testid="project-name-input"
                name="name"
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.projects.form.name"),
                  },
                }}
                disabled={!hasPermission(canManageProjectManagement)}
              />
            </FormField>
            {!isEditMode && (
              <>
                <FormField label={t("page.projects.form.language")}>
                  <FormSelect<CreateOrEditProjectFormValues, LanguageDto>
                    data-testid="project-language-select"
                    id="language"
                    name="language"
                    keySelector={(x) => x.id}
                    items={languages}
                    renderOption={(x) => x.description}
                    disabled={!hasPermission(canManageProjectManagement)}
                  />
                </FormField>
                <FormField label={t("page.projects.form.clone")} required>
                  <FormSearchableSelect<CreateOrEditProjectFormValues, ConnectedProjectDto, string>
                    data-testid="project-clone-select"
                    id="clone"
                    name="cloneProject"
                    control={formMethods.control}
                    items={allowedCloneProjects}
                    placeholder={t("page.projects.form.clone.none")}
                    searchableFieldSelector={(project) => [project.name, project.maintenance, project.owner]}
                    renderOption={(project) => project.name}
                    keySelector={(project) => project.id}
                    rules={{
                      required: t("components.form.error.required", {
                        inputName: t("page.projects.form.clone"),
                      }),
                    }}
                    emptyItem={t("page.projects.form.clone.none")}
                  />
                </FormField>
              </>
            )}
            {isEditMode && (
              <FormField label={t("page.projects.form.slug")} required>
                <Inline gap="sm" justify="center" align="center" wrap={false}>
                  <FormInput name="slug" readOnly />
                  {hasPermission(canManageProjectManagement) && (
                    <IconButton
                      variant="subtle"
                      title={t("page.projects.form.slug.edit")}
                      onClick={slugModalHandlers.setTrue}
                      icon={<Edit05Icon />}
                    />
                  )}
                </Inline>
              </FormField>
            )}
            <FormField label={t("page.projects.form.city")} required>
              <FormInputAutocomplete<CreateOrEditProjectFormValues>
                data-testid="project-city-input"
                name="city"
                suggestions={citySuggestions}
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.projects.form.city"),
                  },
                }}
                disabled={!hasPermission(canManageProjectManagement)}
              />
            </FormField>
            <FormField label={t("page.projects.form.owner")} required>
              <FormInputAutocomplete<CreateOrEditProjectFormValues>
                data-testid="project-owner-input"
                name="owner"
                suggestions={ownerSuggestions}
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.projects.form.owner"),
                  },
                }}
                disabled={!hasPermission(canManageProjectManagement)}
              />
            </FormField>
            <FormField label={t("page.projects.form.maintenance")} required>
              <FormInputAutocomplete<CreateOrEditProjectFormValues>
                data-testid="project-maintainer-input"
                name="maintenance"
                suggestions={maintenanceSuggestions}
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.projects.form.maintenance"),
                  },
                }}
                disabled={!hasPermission(canManageProjectManagement)}
              />
            </FormField>
            {sessionUser.isSuperAdmin && (
              <FormField label={t("page.projects.form.type")}>
                <FormSelect<CreateOrEditProjectFormValues, CreateOrEditProjectFormValues["type"]>
                  data-testid="project-type-select"
                  name="type"
                  items={["addressBased", "companyBased"]}
                  keySelector={(x) => x}
                  renderOption={(x) => (x === "addressBased" ? "address" : "company")}
                  disabled={!hasPermission(canManageProjectManagement)}
                />
              </FormField>
            )}
            {sessionUser.isSuperAdmin && (
              <FormField label={t("page.projects.form.development-type")}>
                <FormSelect<CreateOrEditProjectFormValues, CreateOrEditProjectFormValues["developmentType"]>
                  data-testid="project-development-type-select"
                  name="developmentType"
                  keySelector={(x) => x}
                  items={["existingProperty", "newDevelopment"]}
                  renderOption={(x) => {
                    if (x === "existingProperty") {
                      return t("page.projects.form.development-type.existing-property");
                    } else {
                      return t("page.projects.form.development-type.new-development");
                    }
                  }}
                  disabled={!hasPermission(canManageProjectManagement)}
                />
              </FormField>
            )}
            {sessionUser.isSuperAdmin && (
              <FormField label={t("page.projects.form.app-brand")}>
                <FormSelect<CreateOrEditProjectFormValues, CreateOrEditProjectFormValues["appBranding"]>
                  data-testid="project-branded-app-select"
                  name="appBranding"
                  keySelector={(x) => x}
                  items={appBrands.map((brand) => brand.id)}
                  renderOption={(x) => appBrands.find((brand) => brand.id === x)?.appName || x}
                  disabled={!hasPermission(canManageProjectManagement)}
                />
              </FormField>
            )}
            {isEditMode && (
              <FormField label={t("page.projects.form.language")}>
                <FormSelect<CreateOrEditProjectFormValues, LanguageDto>
                  data-testid="project-language-select"
                  id="language"
                  name="language"
                  keySelector={(x) => x.id}
                  items={languages}
                  renderOption={(x) => x.description}
                  disabled
                />
              </FormField>
            )}
            {sessionUser.isSuperAdmin && (
              <FormField label={t("page.projects.form.allow-hide-post-for-admin")}>
                <FormRadioCardGroup>
                  <FormRadioCard<CreateOrEditProjectFormValues, "allowHidePostForAdmin">
                    name="allowHidePostForAdmin"
                    description={t("page.projects.form.allow-hide-post-for-admin.yes.description")}
                    value="true"
                    label={t("page.projects.form.allow-hide-post-for-admin.yes")}
                  />
                  <FormRadioCard<CreateOrEditProjectFormValues, "allowHidePostForAdmin">
                    name="allowHidePostForAdmin"
                    description={t("page.projects.form.allow-hide-post-for-admin.no.description")}
                    value="false"
                    label={t("page.projects.form.allow-hide-post-for-admin.no")}
                  />
                </FormRadioCardGroup>
              </FormField>
            )}
            {isEditMode && sessionUser.isSuperAdmin && (
              <FormField label={t("page.projects.form.housemate-invites-enabled")}>
                <FormRadioCardGroup>
                  <FormRadioCard<CreateOrEditProjectFormValues, "housemateInvitesEnabled">
                    name="housemateInvitesEnabled"
                    description={t("page.projects.form.housemate-invites-enabled.yes.description")}
                    value="true"
                    label={t("page.projects.form.housemate-invites-enabled.yes")}
                  />
                  <FormRadioCard<CreateOrEditProjectFormValues, "housemateInvitesEnabled">
                    name="housemateInvitesEnabled"
                    description={t("page.projects.form.housemate-invites-enabled.no.description")}
                    value="false"
                    label={t("page.projects.form.housemate-invites-enabled.no")}
                  />
                </FormRadioCardGroup>
              </FormField>
            )}
            {isEditMode && projectState !== "active" && (
              <FormField label={t("page.projects.form.state")}>
                <Inline gap="xl" align="center">
                  {projectState === "closed" && <p className="my-space-sm">🔴 {getProjectState(t, projectState)}</p>}
                  {projectState !== "closed" && (
                    <FormSelect<CreateOrEditProjectFormValues, CreateOrEditProjectFormValues["activeState"]>
                      data-testid="project-state-select"
                      name="activeState"
                      items={projectStateOptions}
                      keySelector={(state) => state}
                      groupSelector={(state) => getProjectStateGroup(t, state)}
                      renderOption={(state) => getProjectState(t, state)}
                      disabled={!sessionUser.isSuperAdmin || !hasPermission(canManageProjectManagement)}
                    />
                  )}
                </Inline>
              </FormField>
            )}
            {isEditMode && sessionUser.isSuperAdmin && projectState !== "closed" && (
              <aside className="flex w-full flex-col items-start gap-space-sm rounded-sm bg-background-danger-secondary p-space-sm">
                <span className="text-caption-emphasis text-text-danger">{t("page.projects.form.danger-zone")}</span>
                <Spread space="sm">
                  <p className="text-body">{t("page.projects.form.danger-zone.description")}</p>
                  <Button variant="filled" tone="error" onClick={closeProjectModalHandlers.setTrue}>
                    {t("common.action.delete")}
                  </Button>
                </Spread>
              </aside>
            )}
          </Form.Body>
          {hasPermission(canManageProjectManagement) && (
            <Form.Footer
              renderPrimaryAction={() => (
                <Button
                  data-testid="project-submit-btn"
                  onClick={formMethods.handleSubmit((data) => {
                    if (isEditMode && data.activeState === "test" && defaultValues.activeState !== "test") {
                      setProjectToTestModalHandlers.setTrue();
                    } else if (isEditMode && data.activeState === "active" && defaultValues.activeState !== "active") {
                      activateProjectModalHandlers.setTrue();
                    } else {
                      return onSubmit(data);
                    }
                  })}
                  isLoading={isSubmitting}
                >
                  {isEditMode ? t("page.projects.edit.submit") : t("page.projects.new.submit")}
                </Button>
              )}
            />
          )}
        </Form.Container>
      </Form.Root>
      {hasPermission(canManageProjectManagement) && (
        <>
          <SlugModal
            isOpened={isSlugModalOpen}
            onOpenChange={slugModalHandlers.set}
            initialSlug={defaultValues.slug || ""}
            takenSlugs={takenSlugs}
            onSubmit={(slug) => {
              setValue("slug", slug);
              slugModalHandlers.setFalse();
            }}
          />
          <CloseProjectModal
            isOpened={isCloseProjectModalOpened || isClosingProject}
            onOpenChange={closeProjectModalHandlers.set}
            onSubmit={async (deleteUsers) => {
              await closeProject(deleteUsers);
              closeProjectModalHandlers.setFalse();
            }}
            projectName={defaultValues.name!}
            isLoading={isClosingProject}
          />
          <ConfirmModal
            tone="primary"
            title={t("page.projects.activate-project.modal.title")}
            description={t("page.projects.activate-project.modal.description")}
            renderDescription={() => (
              <Trans i18nKey="page.projects.activate-project.modal.description" components={{ b: <strong /> }} />
            )}
            isLoading={isSubmitting}
            onReject={(e) => {
              e.preventDefault();
              e.stopPropagation();
              activateProjectModalHandlers.setFalse();
            }}
            onOpenChange={activateProjectModalHandlers.set}
            rejectBtnProps={{
              "data-testid": "activate-project-modal-cancel",
            }}
            onResolve={() => onSubmit(formMethods.getValues())}
            resolveBtnProps={{
              text: t("page.projects.activate-project.modal.confirm"),
              "data-testid": "activate-project-modal-confirm",
            }}
            isOpened={isActivateProjectModalOpen}
            shouldCloseOnEsc
          />
          <ConfirmModal
            title={t("page.projects.set-project-to-test.modal.title")}
            description={t("page.projects.set-project-to-test.modal.description")}
            isLoading={isSubmitting}
            onReject={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setProjectToTestModalHandlers.setFalse();
            }}
            onOpenChange={setProjectToTestModalHandlers.set}
            rejectBtnProps={{
              "data-testid": "set-project-to-test-modal-cancel",
            }}
            onResolve={() => onSubmit(formMethods.getValues())}
            resolveBtnProps={{
              text: t("page.projects.set-project-to-test.modal.confirm"),
              "data-testid": "set-project-to-test-modal-confirm",
            }}
            isOpened={isSetProjectToTestModalOpen}
            shouldCloseOnEsc
            tone="warning"
          />
        </>
      )}
    </DocumentPage>
  );
}

function getProjectState(t: TranslationFunction, state?: ProjectDetailedDto["activeState"]) {
  switch (state) {
    case "unknown":
      return t("page.projects.form.state.inactive");
    case "active":
      return t("page.projects.form.state.active");
    case "closed":
      return t("page.projects.form.state.closed");
    case "test":
      return t("page.projects.form.state.test");
    case "demo":
      return t("page.projects.form.state.demo");
    default:
      return "???";
  }
}

function getProjectStateGroup(t: TranslationFunction, state: ProjectDetailedDto["activeState"]) {
  return state === "unknown" || state === "active"
    ? t("page.projects.form.state.group.production")
    : t("page.projects.form.state.group.internal");
}
